import * as THREE from "three";

/** Hauteur de la fumée (0 à 1) d'un objet qui en émet (`DecorModel.smokes`), quand l'élément n'en fixe pas. */
export const DEFAULT_DECOR_SMOKE = 0.5;

/** Le nœud d'un modèle d'où part sa fumée. */
export const SMOKE_SOURCE_NODE = "SmokeSource";

const MAX_PUFFS = 80;
/** Durée de vie et vitesse de montée d'une volute, du panache le plus bas au plus haut. */
const LIFE_S: [number, number] = [2.2, 4.8];
const RISE_M_PER_S: [number, number] = [0.28, 0.6];
const END_RADIUS_M = 0.33;
const DRIFT_M_PER_S = 0.07;
const START_RADIUS_M = 0.025;
/** Volutes émises par mètre de montée : la densité reste la même quelle que soit la hauteur. */
const PUFFS_PER_METER = 16;
const OPACITY = 0.47;
const PREWARM_STEP_S = 1 / 30;
/** Au-delà de ce déplacement de la source, un panache figé se reforme à sa nouvelle place. */
const FROZEN_MOVE_M = 0.01;

const puffGeometry = new THREE.IcosahedronGeometry(1, 0);

const lerp = ([from, to]: [number, number], t: number) => from + (to - from) * t;

interface Puff {
  mesh: THREE.Mesh;
  material: THREE.MeshStandardMaterial;
  age: number;
  life: number;
  rise: number;
  endRadius: number;
  origin: THREE.Vector3;
  drift: THREE.Vector2;
  spin: number;
}

/**
 * Le panache d'un fumeur, dans le repère de la scène du décor (en mètres) : des volutes facettées
 * qui montent de `source`, dérivent, gonflent et s'effacent. `intensity` (0 à 1) règle la hauteur
 * qu'elles atteignent ; à 0, il n'y a plus de fumée.
 */
export class SmokePlume {
  readonly group = new THREE.Group();
  private readonly puffs: Puff[] = [];
  private pending = 0;
  private seed = 1;
  private frozen: { source: THREE.Vector3; level: number } | null = null;

  constructor() {
    this.group.name = "SmokePlume";
  }

  /**
   * Animations arrêtées (`delta` nul), le panache reste figé tel qu'il serait après quelques secondes :
   * on le reforme quand la source bouge ou que l'intensité change.
   */
  update(source: THREE.Vector3, intensity: number, scale: number, delta: number): void {
    const level = Math.max(0, Math.min(1, intensity));
    this.group.visible = level > 0;
    if (!delta) {
      const stale = !this.frozen || this.frozen.level !== level || this.frozen.source.distanceTo(source) > FROZEN_MOVE_M;
      if (stale) this.prewarm(source, level, scale);
      return;
    }
    this.frozen = null;
    this.step(source, level, scale, delta);
  }

  private prewarm(source: THREE.Vector3, level: number, scale: number): void {
    this.clear();
    for (let t = 0; t < lerp(LIFE_S, level) * 1.25; t += PREWARM_STEP_S) this.step(source, level, scale, PREWARM_STEP_S);
    this.frozen = { source: source.clone(), level };
  }

  private step(source: THREE.Vector3, level: number, scale: number, delta: number): void {
    if (level > 0) {
      this.pending += delta * PUFFS_PER_METER * lerp(RISE_M_PER_S, level);
      while (this.pending >= 1) {
        this.pending -= 1;
        this.spawn(source, level, scale);
      }
    }

    for (let i = this.puffs.length - 1; i >= 0; i--) {
      const puff = this.puffs[i]!;
      puff.age += delta;
      const t = puff.age / puff.life;
      if (t >= 1) {
        this.group.remove(puff.mesh);
        puff.material.dispose();
        this.puffs.splice(i, 1);
        continue;
      }
      const radius = (START_RADIUS_M + (puff.endRadius - START_RADIUS_M) * Math.sqrt(t)) * scale;
      puff.mesh.scale.setScalar(radius);
      puff.mesh.position.set(
        puff.origin.x + puff.drift.x * puff.age * scale,
        puff.origin.y + puff.rise * puff.age * scale,
        puff.origin.z + puff.drift.y * puff.age * scale,
      );
      puff.mesh.rotation.y += puff.spin * delta;
      puff.material.opacity = OPACITY * Math.sin(Math.PI * Math.min(1, t * 1.6 + 0.08)) * (1 - t);
    }
  }

  dispose(): void {
    this.clear();
  }

  private clear(): void {
    for (const puff of this.puffs) puff.material.dispose();
    this.puffs.length = 0;
    this.pending = 0;
    this.group.clear();
  }

  private spawn(source: THREE.Vector3, level: number, scale: number): void {
    if (this.puffs.length >= MAX_PUFFS) return;
    const material = new THREE.MeshStandardMaterial({
      name: "smoke",
      color: 0xb3b8be,
      roughness: 1,
      metalness: 0,
      flatShading: true,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(puffGeometry, material);
    mesh.position.copy(source);
    mesh.scale.setScalar(START_RADIUS_M * scale);
    mesh.rotation.set(this.random() * Math.PI, this.random() * Math.PI, 0);
    const angle = this.random() * Math.PI * 2;
    this.puffs.push({
      mesh,
      material,
      age: 0,
      life: lerp(LIFE_S, level) * (0.75 + this.random() * 0.5),
      rise: lerp(RISE_M_PER_S, level),
      endRadius: END_RADIUS_M * (0.8 + this.random() * 0.4),
      origin: source.clone(),
      drift: new THREE.Vector2(DRIFT_M_PER_S + Math.cos(angle) * 0.03, Math.sin(angle) * 0.03),
      spin: (this.random() - 0.5) * 1.5,
    });
    this.group.add(mesh);
  }

  private random(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return this.seed / 2147483647;
  }
}
